<template>
  <div>
    <div class="page-section">
      <div :class="containerClass">
        <!-- ============== Filters ============== -->
        <div class="card analytics-card toolbar-card mb-4">
          <div class="card-body">
            <div class="toolbar-controls">
              <div class="control control--md">
                <label>Date Range</label>
                <date-picker
                  v-model="filters.dateRange"
                  format="MMM DD, YYYY"
                  valueType="YYYY-MM-DD"
                  range
                  class="w-100"
                  placeholder="Start - End"
                />
              </div>
              <div class="control">
                <label>Course</label>
                <v-select
                  class="form-control v-select-custom"
                  label="title"
                  v-model="filters.course"
                  :reduce="o => o.id"
                  :options="allCourses"
                  placeholder="Select a course..."
                  :clearable="true"
                />
              </div>
              <div class="control">
                <label>Assignment</label>
                <!-- Gated on course: the assignment list is only meaningful once
                     there is a course to draw it from. -->
                <v-select
                  class="form-control v-select-custom"
                  label="assignment_title"
                  v-model="filters.assignment"
                  :reduce="o => o.assignment_id"
                  :options="assignmentOptions"
                  :disabled="!filters.course"
                  :loading="isAssignmentsLoading"
                  :clearable="true"
                  :placeholder="filters.course ? 'All assignments' : 'Select a course first'"
                >
                  <template #no-options>
                    No assignments have been submitted in this course.
                  </template>
                </v-select>
              </div>
            </div>
          </div>
        </div>

        <!-- ============== 1. Overview ============== -->
        <div class="card analytics-card mb-4">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Assignment Analytics Overview</h5>
                <p class="section-sub">{{ overviewSubtitle }}</p>
              </div>
            </div>

            <div v-if="isOverviewLoading" class="text-center py-4">
              <b-spinner class="align-middle mr-2" /><strong>Loading overview...</strong>
            </div>
            <div v-else-if="!overview.assigned_count && !overview.submitted_count"
              class="text-center text-muted py-4">
              No assignment work matches the current filters.
            </div>
            <div v-else class="kpi-grid">
              <div v-for="card in kpiCards" :key="card.label" class="kpi-card">
                <div class="kpi-card__header">
                  <div :class="['kpi-card__icon', card.iconClass]">
                    <i class="material-icons">{{ card.icon }}</i>
                  </div>
                  <span class="kpi-card__label">{{ card.label }}</span>
                </div>
                <div class="kpi-card__value">
                  {{ card.value }}<span v-if="card.suffix" class="kpi-card__suffix">{{ card.suffix }}</span>
                </div>
                <div class="kpi-card__hint">{{ card.hint }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============== 2. Submissions over time ============== -->
        <div class="card analytics-card mb-4">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Assignment Submissions Over Time</h5>
                <p class="section-sub">Submissions per period, including repeat attempts.</p>
              </div>
              <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                <v-select
                  class="form-control v-select-custom chart-control"
                  label="label"
                  v-model="activity.timeFrame"
                  :reduce="o => o.value"
                  :options="timeFrameOptions"
                  :clearable="false"
                />
                <v-select
                  class="form-control v-select-custom chart-control"
                  label="assignment_title"
                  v-model="activity.assignmentId"
                  :reduce="o => o.assignment_id"
                  :options="allAssignments"
                  :clearable="true"
                  placeholder="All Assignments"
                />
              </div>
            </div>

            <div v-show="isActivityLoading" class="text-center py-4">
              <b-spinner class="align-middle mr-2" /><strong>Loading activity...</strong>
            </div>
            <!-- Kept mounted rather than v-if'd: Chart.js needs a canvas that
                 survives the loading state, otherwise it draws into a node that
                 was never in the document and the chart comes out blank. -->
            <div v-show="!isActivityLoading">
              <div class="chart-container">
                <canvas ref="activityCanvas"></canvas>
              </div>
              <p v-if="activityRangeLabel" class="chart-caption">{{ activityRangeLabel }}</p>
            </div>
          </div>
        </div>

        <!-- ============== 3. Detailed report ============== -->
        <div class="card analytics-card mb-4">
          <div class="card-body">
            <div class="section-header">
              <div>
                <h5 class="section-title">Generate Detailed Report</h5>
                <p class="section-sub">
                  A CSV over the current filters. Tick the columns you need.
                </p>
              </div>
            </div>

            <div class="report-row">
              <div class="control control--grow">
                <label>Students</label>
                <v-select
                  class="form-control v-select-custom"
                  label="name"
                  v-model="filters.student"
                  :reduce="o => o.student_id"
                  :options="studentOptions"
                  :loading="isStudentsLoading"
                  :clearable="true"
                  placeholder="All Students"
                >
                  <template #option="opt">
                    <div class="picker-option">
                      <span class="picker-option__title">{{ opt.name || opt.email }}</span>
                      <span class="picker-option__meta">{{ opt.email }}</span>
                    </div>
                  </template>
                  <template #no-options>
                    No students have assignment work in this scope.
                  </template>
                </v-select>
              </div>
              <b-btn
                variant="primary"
                class="d-inline-flex align-items-center report-btn"
                :disabled="isReportLoading"
                @click.prevent="generateReport"
              >
                <i v-if="isReportLoading" class="fas fa-circle-notch fa-spin mr-1"></i>
                <span v-else class="material-icons icon-16pt mr-1">file_download</span>
                Generate Report
              </b-btn>
            </div>

            <div class="report-toggles mt-3">
              <b-form-checkbox
                v-for="opt in reportOptions"
                :key="opt.key"
                v-model="reportInclude[opt.key]"
              >
                {{ opt.label }}
              </b-form-checkbox>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { debounce } from 'lodash';
import DatePicker from 'vue2-datepicker';
import vSelect from 'vue-select';
import moment from 'moment';
import { Chart } from 'chart.js';
import Page from '@/components/Page.vue';
import { formatToAPIDate } from '../../../common/utils';

const defaultRange = () => [
  formatToAPIDate(moment().startOf('month')),
  formatToAPIDate(moment().endOf('month')),
];

// Fixed chart palette, matching the assessment page.
const SERIES_PRIMARY = '#2a78d6';
const GRID_COLOR = 'rgba(148, 163, 184, 0.2)';
const AXIS_TEXT = '#64748b';

const defaultReportInclude = () => ({
  grade_percent: true,
  status: true,
  resubmissions: true,
  time_spent: true,
  submitted_date: true,
});

export default {
  components: { DatePicker, vSelect },
  extends: Page,
  data() {
    return {
      title: 'Assignment Analytics',

      isBooting: true,
      allCourses: [],

      filters: {
        dateRange: defaultRange(),
        course: null,
        assignment: null,
        student: null,
      },

      // Assignments within the selected course, for the page filter.
      assignmentOptions: [],
      isAssignmentsLoading: false,
      // Every assignment in scope, for the chart's own scope select.
      allAssignments: [],

      studentOptions: [],
      isStudentsLoading: false,

      overview: {},
      isOverviewLoading: false,

      activity: { timeFrame: 'monthly', assignmentId: null },
      activityPayload: {},
      isActivityLoading: false,
      activityChart: null,

      timeFrameOptions: [
        { value: 'weekly', label: 'Weekly' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' },
      ],

      reportOptions: [
        { key: 'grade_percent', label: 'Grade (%)' },
        { key: 'status', label: 'Status' },
        { key: 'resubmissions', label: 'Resubmissions' },
        { key: 'time_spent', label: 'Time Spent' },
        { key: 'submitted_date', label: 'Submitted Date' },
      ],
      reportInclude: defaultReportInclude(),
      isReportLoading: false,
    };
  },
  computed: {
    kpiCards() {
      const o = this.overview;
      return [
        {
          label: 'Assignments Submitted',
          icon: 'assignment_turned_in',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(o.submitted_count),
          hint: `of ${this.formatInt(o.assigned_count)} assigned`,
        },
        {
          label: 'Completion Rate',
          icon: 'check_circle',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(o.completion_rate),
          suffix: '%',
          hint: 'submitted of assigned',
        },
        {
          label: 'Avg Grade',
          icon: 'grade',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatPct(o.avg_grade),
          suffix: '%',
          hint: 'graded work only',
        },
        {
          label: 'Graded',
          icon: 'playlist_add_check',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(o.graded_count),
          // Grading is per attempt; submitted_count is now distinct slots.
          hint: `of ${this.formatInt(o.submission_events_count)} submissions`,
        },
        {
          label: 'Pending Review',
          icon: 'hourglass_empty',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(o.pending_review_count),
          hint: 'awaiting instructor grade',
        },
        {
          label: 'Resubmissions',
          icon: 'autorenew',
          iconClass: 'kpi-card__icon--grey',
          value: this.formatInt(o.resubmission_count),
          // Not "returned for revision" - no such workflow exists. This counts
          // students who handed the same assignment in more than once.
          hint: 'submitted more than once',
        },
      ];
    },
    overviewSubtitle() {
      const o = this.overview;
      if (!o.assignments_assessed) {
        return 'Graded and pending cover work on assignments that require a grade.';
      }
      return `${this.formatInt(o.assignments_assessed)} assignments across `
        + `${this.formatInt(o.students_assessed)} students. `
        + 'Graded and pending cover work on assignments that require a grade.';
    },
    activityRangeLabel() {
      const p = this.activityPayload;
      if (!p.date_from || !p.date_to) return '';
      return `(${p.date_from} → ${p.date_to})`;
    },
    baseParams() {
      const range = this.filters.dateRange || [];
      const params = {
        date_from: range[0] || null,
        date_to: range[1] || null,
      };
      if (this.filters.course) params.course_id = this.filters.course;
      if (this.filters.assignment) params.assignment_id = this.filters.assignment;
      return params;
    },
  },
  methods: {
    ...mapActions('lmsAnalytics', [
      'getAssignmentAnalyticsOverview',
      'getAssignmentLeaderboard',
      'getAssignmentActivityGraph',
      'getAssignmentStudents',
      'generateAssignmentReport',
    ]),
    ...mapActions('courses', ['getLmsCourses']),
    ...mapActions('fileDownload', ['downloadFile']),

    formatPct(v) {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      return Number.isNaN(n) ? '0' : n.toFixed(1);
    },
    formatInt(v) {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      return Number.isNaN(n) ? '0' : n.toLocaleString();
    },
    unwrapEnvelope(res) {
      const payload = res && res.data !== undefined ? res.data : res;
      return payload || {};
    },
    unwrapListEnvelope(res) {
      const payload = res && res.data ? res.data : res;
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.results)) return payload.results;
      if (payload.data) {
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.data.results)) return payload.data.results;
      }
      return [];
    },
    unwrapUrl(res) {
      return (res && res.data && res.data.url) || (res && res.url) || null;
    },
    validateRange(maxMonths = 24) {
      const r = this.filters.dateRange;
      if (!r || !r[0] || !r[1]) return true;
      const diff = moment(r[1]).diff(moment(r[0]), 'months', true);
      if (diff > maxMonths) {
        this.makeToast({ variant: 'danger', msg: `Date range must be ${maxMonths} months or less.` });
        return false;
      }
      return true;
    },
    async runDownload(url) {
      try {
        await this.downloadFile({ fileUrl: url });
        this.makeToast({ variant: 'success', msg: 'File downloaded.' });
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to download file.' });
      }
    },

    // -------- Lookups --------
    async fetchLookups() {
      try {
        const coursesRes = await this.getLmsCourses({ limit: 200 });
        this.allCourses = this.unwrapListEnvelope(coursesRes).map(c => ({ id: c.id, title: c.title }));
      } catch (e) { /* silent */ }
    },

    async fetchAssignments() {
      if (!this.validateRange(24)) return;
      this.isAssignmentsLoading = true;
      try {
        const range = this.filters.dateRange || [];
        const scopeParams = { date_from: range[0] || null, date_to: range[1] || null };

        const allRes = await this.getAssignmentLeaderboard(scopeParams);
        this.allAssignments = this.unwrapListEnvelope(allRes);

        if (this.filters.course) {
          const courseRes = await this.getAssignmentLeaderboard({
            ...scopeParams, course_id: this.filters.course,
          });
          this.assignmentOptions = this.unwrapListEnvelope(courseRes);
        } else {
          this.assignmentOptions = [];
        }

        // Drop a selection that has fallen out of the current course/date scope.
        if (this.filters.assignment
          && !this.assignmentOptions.some(r => r.assignment_id === this.filters.assignment)) {
          this.filters.assignment = null;
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load assignments.' });
      }
      this.isAssignmentsLoading = false;
    },

    async fetchStudents() {
      this.isStudentsLoading = true;
      try {
        const res = await this.getAssignmentStudents(this.baseParams);
        this.studentOptions = this.unwrapListEnvelope(res);
        // Same rule as the assignment picker: a student who no longer has work
        // in scope must not stay selected and silently empty the report.
        if (this.filters.student
          && !this.studentOptions.some(s => s.student_id === this.filters.student)) {
          this.filters.student = null;
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load students.' });
      }
      this.isStudentsLoading = false;
    },

    // -------- Overview --------
    async fetchOverview() {
      if (!this.validateRange(24)) return;
      this.isOverviewLoading = true;
      try {
        const res = await this.getAssignmentAnalyticsOverview(this.baseParams);
        this.overview = this.unwrapEnvelope(res);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load the assignment overview.' });
      }
      this.isOverviewLoading = false;
    },

    // -------- Activity --------
    async fetchActivity() {
      if (!this.validateRange(24)) return;
      this.isActivityLoading = true;
      try {
        // The chart carries its own assignment scope select, so it takes the
        // course/date scope from the page but ignores the page's assignment
        // filter - two controls pointing at the same axis would be ambiguous.
        const params = { ...this.baseParams, time_frame: this.activity.timeFrame };
        delete params.assignment_id;
        if (this.activity.assignmentId) params.assignment_id = this.activity.assignmentId;
        const res = await this.getAssignmentActivityGraph(params);
        this.activityPayload = this.unwrapEnvelope(res);
        this.$nextTick(this.renderActivityChart);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load assignment activity.' });
      }
      this.isActivityLoading = false;
    },

    // -------- Report --------
    async generateReport() {
      if (!this.validateRange(24)) return;
      this.isReportLoading = true;
      try {
        const payload = { ...this.baseParams, include: this.reportInclude };
        if (this.filters.student) payload.student_id = this.filters.student;
        const res = await this.generateAssignmentReport(payload);
        const url = this.unwrapUrl(res);
        if (url) await this.runDownload(url);
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: 'Failed to generate the assignment report.' });
      }
      this.isReportLoading = false;
    },

    // -------- Charts --------
    renderActivityChart() {
      const ctx = this.$refs.activityCanvas;
      if (!ctx) return;
      if (this.activityChart) this.activityChart.destroy();

      const payload = this.activityPayload;
      this.activityChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: payload.labels || [],
          datasets: [{
            label: 'Assignments Submitted',
            data: payload.datasets || [],
            backgroundColor: SERIES_PRIMARY,
            barPercentage: 0.6,
            categoryPercentage: 0.8,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'top' },
          scales: {
            yAxes: [{
              ticks: { beginAtZero: true, fontColor: AXIS_TEXT },
              gridLines: { color: GRID_COLOR, drawBorder: false },
            }],
            xAxes: [{
              ticks: { fontColor: AXIS_TEXT },
              gridLines: { display: false },
            }],
          },
        },
      });
    },
    destroyCharts() {
      if (this.activityChart) {
        try { this.activityChart.destroy(); } catch (e) { /* noop */ }
        this.activityChart = null;
      }
    },

    refreshAll() {
      this.fetchOverview();
      this.fetchActivity();
      this.fetchStudents();
    },

    // -------- Filter watchers --------
    // All three bail while booting: seeding the filters from the query string
    // trips the watchers before the first load has run, and without the guard
    // every endpoint is hit twice with the later response winning.
    onScopeChange() {
      if (this.isBooting) return;
      this.fetchAssignments();
      this.debouncedRefreshAll();
    },
    onCourseChange() {
      if (this.isBooting) return;
      // A course change invalidates the assignment list, so drop the selection
      // rather than filtering by an assignment from a different course. That
      // write re-enters onFilterChange, which the debounce collapses.
      this.filters.assignment = null;
      this.fetchAssignments();
      this.debouncedRefreshAll();
    },
    onFilterChange() {
      if (this.isBooting) return;
      this.debouncedRefreshAll();
    },
  },
  watch: {
    'filters.dateRange': 'onScopeChange',
    'filters.course': 'onCourseChange',
    'filters.assignment': 'onFilterChange',
    'activity.timeFrame': 'fetchActivity',
    'activity.assignmentId': 'fetchActivity',
  },
  created() {
    this.debouncedRefreshAll = debounce(this.refreshAll, 250);
  },
  async mounted() {
    const q = this.$route.query || {};
    if (q.date_from && q.date_to) this.filters.dateRange = [q.date_from, q.date_to];
    if (q.course_id) this.filters.course = q.course_id;

    await this.fetchLookups();
    await this.fetchAssignments();

    // Deep link straight into one assignment.
    if (q.assignment_id
      && this.assignmentOptions.some(r => r.assignment_id === q.assignment_id)) {
      this.filters.assignment = q.assignment_id;
    }

    // Let the query-string writes above flush through the watchers while they
    // are still guarded, then take the first load ourselves.
    await this.$nextTick();
    this.isBooting = false;
    this.refreshAll();
  },
  beforeDestroy() {
    this.destroyCharts();
  },
};
</script>

<style scoped lang="scss">
.analytics-card {
  border: 1px solid #e7ecf5;
  border-radius: 16px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05);
  background: #fff;
}

.toolbar-card { position: sticky; top: 8px; z-index: 5; }

/* ---- toolbar ---- */
.toolbar-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.control {
  flex: 1 1 170px;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  label {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin: 0;
  }
}
.control--md { flex: 1 1 220px; min-width: 200px; }
.control--grow { flex: 1 1 auto; }

.picker-option__title { display: block; font-weight: 600; color: #0f172a; }
.picker-option__meta { display: block; font-size: 0.75rem; color: #94a3b8; }

/* ---- KPI tiles ---- */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 16px;
}
.kpi-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid #eef2f7;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 12px 22px rgba(15, 23, 42, 0.08); }
}
.kpi-card__header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.kpi-card__icon {
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex: 0 0 auto;
  i { font-size: 18px; color: #fff; }
  &--blue   { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
  &--green  { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
  &--red    { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
  &--purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
  &--orange { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
  &--grey   { background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%); }
}
.kpi-card__label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.kpi-card__value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.kpi-card__suffix { font-size: 0.95rem; color: #6b7280; }
.kpi-card__hint { margin-top: 6px; font-size: 0.74rem; color: #94a3b8; }

/* ---- sections ---- */
.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 16px;
  flex-wrap: wrap;
}
.section-title {
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 4px;
  font-size: 1.05rem;
  letter-spacing: 0.01em;
}
.section-sub { margin: 0; color: #64748b; font-size: 0.85rem; }

.chart-control { width: 170px; min-width: 150px; }
.chart-container { position: relative; height: 320px; }
.chart-caption {
  margin: 8px 0 0;
  text-align: center;
  font-size: 0.76rem;
  color: #94a3b8;
}

/* ---- report ---- */
.report-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}
.report-btn { flex: 0 0 auto; height: 38px; }
.report-toggles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px 16px;
}
</style>
